import fetchClient from 'lib/fetch_client'
import routes from 'server_routes'
import { getDefaultCurrencyCode } from 'griffin'
import { colocate } from 'shared/helpers/obj_helpers'
import { encodeCampaignId } from 'shared/campaign_url'
import { serializeCampaignToServer as serialize } from '../../lib/serializer'
import { AMOUNT_TYPES, STORAGE_EXPIRATION } from 'shared/shared_consts'
import { endOfDay } from 'shared/helpers/datetime_helpers'
import { INFLATE as INFLATE_USER_TXNS } from './user_txns'
import { getCampaignSelector } from 'redux/selectors/campaign'
import { saveTxnInCookie } from 'lib/cookies_utils'
import { trackImpression } from 'lib/fpti_analytics'
import { LOCATION_CHANGE } from 'react-router-redux'
import * as venice from 'lib/venice_bridge'
import { CREATE_DNW_TRANSACTION_SUCCESS } from './dnw_transaction'

export const NEW_CAMPAIGN_ID = '__new_campaign__'

const DEFAULT_PAN = 50

const CREATE = 'pools/campaign/CREATE'
const DELETE = 'pools/campaign/DELETE'

const SET_DATA = 'pools/campaign/SET_DATA'

const RESET_REMOTE_OPERATION = 'pools/campaign/RESET_REMOTE_OPERATION'
const START_CAMPAIGN_REMOTE_OPERATION =
  'pools/campaign/START_CAMPAIGN_REMOTE_OPERATION'
const FINISH_CAMPAIGN_REMOTE_OPERATION =
  'pools/campaign/FINISH_CAMPAIGN_REMOTE_OPERATION'
const FAILED_CAMPAIGN_REMOTE_OPERATION =
  'pools/campaign/FAILED_CAMPAIGN_REMOTE_OPERATION'

export const CAMPAIGN_CREATED = 'pools/campaign/CAMPAIGN_CREATED'
export const FINISH_CHIP_IN_SUCCESSFULLY =
  'pools/campaign/FINISH_CHIP_IN_SUCCESSFULLY'
export const FINISH_WITHDRAW_SUCCESSFULLY =
  'pools/campaign/FINISH_WITHDRAW_SUCCESSFULLY'

function resetExpiration() {
  return { persistExpiresAt: new Date().valueOf() + STORAGE_EXPIRATION }
}

function setInCampaign(state, id, obj) {
  return { ...state, [id]: { ...state[id], ...obj, ...resetExpiration() } }
}

export default function reducer(state = {}, action) {
  switch (action.type) {
    case START_CAMPAIGN_REMOTE_OPERATION:
      return setInCampaign(state, action.payload.id, {
        operatingRemotely: true,
      })
    case FINISH_CAMPAIGN_REMOTE_OPERATION:
      return setInCampaign(state, action.payload.id, {
        operatingRemotely: false,
        err: null,
      })
    case RESET_REMOTE_OPERATION:
      return {
        ...state,
        ...setInCampaign(state, action.payload.id, {
          operatingRemotely: false,
          err: null,
        }),
        last_creation_id: null,
      }
    case FAILED_CAMPAIGN_REMOTE_OPERATION:
      return setInCampaign(state, action.payload.id, {
        operatingRemotely: false,
        err: action.payload.err,
      })
    case CREATE: {
      const { owner, currencyCode, campaignType } = action.payload
      return {
        ...state,
        [NEW_CAMPAIGN_ID]: {
          id: NEW_CAMPAIGN_ID,
          owner,
          currency: currencyCode,
          campaign_type: campaignType,
          start_date: new Date(),
          current_value: 0,
          can_chip_in: true,
          can_donate: true,
          preview: true, // new campaigns that haven't been saved to server are in preview

          // Default values for new campaign
          share_progress: true,
          share_contributors: true,
          share_contributions: true,
          background_pan: DEFAULT_PAN,
          amount_type: AMOUNT_TYPES.any,
          pledge: 0,

          ...resetExpiration(),
        },
      }
    }
    case DELETE: {
      return setInCampaign(state, action.payload.id, { deleted: true })
    }
    case CAMPAIGN_CREATED:
      return {
        ...state,
        [action.payload.newId]: {
          ...state[NEW_CAMPAIGN_ID],
          id: action.payload.newId,

          // the assumption here is that when we have an id, we're saved in the server, therefore we're
          // no longer in preview mode.
          preview: false,

          ...resetExpiration(),
        },
        last_creation_id: action.payload.newId,
      }
    case SET_DATA:
      return setInCampaign(state, action.payload.id, action.payload.data)
    case INFLATE_USER_TXNS: {
      const id = action.payload.id
      const totalInflatedAmount = action.payload.list.reduce((sum, txn) => {
        return txn.withdraw ? sum : sum + txn.amount
      }, 0)
      const totalMoneyBoxInflatedAmount = action.payload.list.reduce(
        (sum, txn) => {
          return txn.withdraw ? sum - txn.amount : sum
        },
        0
      )
      return {
        ...state,
        [id]: {
          ...state[id],
          current_value:
            (state[id].current_value ? state[id].current_value : 0) +
            totalInflatedAmount,
          moneybox_amount:
            (state[id].moneybox_amount ? state[id].moneybox_amount : 0) +
            totalMoneyBoxInflatedAmount,
          ...resetExpiration(),
        },
      }
    }
    case LOCATION_CHANGE: {
      return {
        ...state,
        operatingRemotely: false,
      }
    }
    case CREATE_DNW_TRANSACTION_SUCCESS: {
      const { amount, campaign_id } = action.payload.txn
      return {
        ...state,
        [campaign_id]: {
          ...state[campaign_id],
          current_value:
            (state[campaign_id].current_value
              ? state[campaign_id].current_value
              : 0) + amount,
          txn_count: state[campaign_id].txn_count + 1,
        },
      }
    }
    default:
      return state
  }
}

export function startRemoteOperation(id) {
  return { type: START_CAMPAIGN_REMOTE_OPERATION, payload: { id } }
}

export function finishRemoteOperation(id) {
  return { type: FINISH_CAMPAIGN_REMOTE_OPERATION, payload: { id } }
}

export function failedCampaignRemoteOperation(id, err) {
  return { type: FAILED_CAMPAIGN_REMOTE_OPERATION, payload: { id, err } }
}

export function resetRemoteOperation(id) {
  return { type: RESET_REMOTE_OPERATION, payload: { id } }
}

export function create(campaignType) {
  return (dispatch, getState) => {
    dispatch({
      type: CREATE,
      payload: {
        owner: getState().current_user,
        currencyCode: getDefaultCurrencyCode(),
        campaignType,
      },
    })
  }
}

export function deleteCampaign(id) {
  return { type: DELETE, payload: { id } }
}

export function campaignCreated(newId) {
  return { type: CAMPAIGN_CREATED, payload: { newId } }
}

export function setData(id, data) {
  if (data.goal != null) {
    data.goal = parseFloat(data.goal)
  }

  if (data.end_date != null) {
    data.end_date = data.end_date
      ? endOfDay(new Date(data.end_date)).toISOString()
      : undefined
  }

  if (data.background_pan != null) {
    data.background_pan = parseInt(data.background_pan, 10)
  }

  if (data.share_progress === false) {
    // YES, this must be like that to differentiate the case where it's not there
    data.share_amount = false
  }

  return { type: SET_DATA, payload: { id, data } }
}

export function createRemotely() {
  return (dispatch, getState) => {
    let campaignData = getCampaignSelector(getState(), {
      campaignId: NEW_CAMPAIGN_ID,
    })
    dispatch(startRemoteOperation(NEW_CAMPAIGN_ID))
    return fetchClient()
      .request(routes.campaign.api.index(), {
        method: 'POST',
        body: serialize(campaignData),
      })
      .then(response => {
        const campaignId = response.campaign.id
        trackImpression({
          pageName: 'campaign-create-success',
          campaignId: campaignId,
        })
        // since the success page doesn't have a route
        venice.campaignCreated()
        dispatch(finishRemoteOperation(NEW_CAMPAIGN_ID))
        dispatch(campaignCreated(encodeCampaignId(campaignId)))
      })
      .catch(err => {
        console.error(err) // eslint-disable-line
        dispatch(failedCampaignRemoteOperation(NEW_CAMPAIGN_ID, err))
      })
  }
}

export function updateRemotely(id, data) {
  return (dispatch, getState) => {
    let campaignData = getCampaignSelector(getState(), { campaignId: id })

    data = colocate(data, 'background_image', 'background_image_filename')

    // skip the server if we're in preview since the campaign doesn't exist there yet
    if (campaignData.preview || campaignData.id === NEW_CAMPAIGN_ID) {
      dispatch(setData(id, data))
      return Promise.resolve()
    }

    const body = serialize(data, { alwaysIncludePan: true })

    if (campaignData.charity) delete body.category

    dispatch(startRemoteOperation(id))
    return fetchClient()
      .request(routes.campaign.api.id({ id }), {
        method: 'PATCH',
        body,
      })
      .then(response => {
        data.can_chip_in = response.can_chip_in
        if (data.title) document.title = data.title
        dispatch(setData(id, data))
        dispatch(finishRemoteOperation(id))
      })
      .catch(err => {
        err.updateData = data
        dispatch(failedCampaignRemoteOperation(id, err))
        return Promise.reject(err)
      })
  }
}

export function deleteRemotely(id) {
  return (dispatch, getState) => {
    dispatch(startRemoteOperation(id))
    return fetchClient()
      .request(routes.campaign.api.id({ id }), {
        method: 'DELETE',
      })
      .then(response => {
        dispatch(deleteCampaign(id))
      })
      .catch(err => {
        dispatch(failedCampaignRemoteOperation(id, err))
        return Promise.reject(err)
      })
  }
}

export function endNowRemotely(id) {
  return (dispatch, getState) => {
    let campaignData = getCampaignSelector(getState(), { campaignId: id })

    // fail it if we're in preview since it's irrelevant
    if (campaignData.preview || campaignData.id === NEW_CAMPAIGN_ID) {
      return Promise.reject()
    }

    dispatch(startRemoteOperation(id))
    return fetchClient()
      .request(routes.campaign.api.end({ id }), {
        method: 'POST',
      })
      .then(response => {
        dispatch(
          setData(id, {
            end_date: response.end_date,
            can_chip_in: response.can_chip_in,
          })
        )
        dispatch(finishRemoteOperation(id))
      })
      .catch(err => {
        dispatch(failedCampaignRemoteOperation(id, err))
        return Promise.reject(err)
      })
  }
}

export function withdrawRemotely(id, amount) {
  return (dispatch, getState) => {
    let campaignData = getCampaignSelector(getState(), { campaignId: id })

    // fail it if we're in preview since it's irrelevant
    if (campaignData.preview || campaignData.id === NEW_CAMPAIGN_ID) {
      return Promise.reject()
    }

    dispatch(startRemoteOperation(id))
    return fetchClient()
      .request(routes.campaign.api.withdraw({ id }), {
        method: 'POST',
        body: { amount },
      })
      .then(response => {
        const { payment_id: paymentId } = response
        const date = new Date().toISOString()
        const txn = {
          paymentId,
          amount,
          campaignId: id,
          pending: true,
          withdraw: true,
          date,
        }
        saveTxnInCookie(id, getState().current_user.id, txn)
        dispatch({
          type: FINISH_WITHDRAW_SUCCESSFULLY,
          payload: { txn, withdrawAmount: amount },
        })
        dispatch(finishRemoteOperation(id))
      })
      .catch(err => {
        dispatch(failedCampaignRemoteOperation(id, err))
        return Promise.reject(err)
      })
  }
}

export function chipInRemotely(id, fields) {
  return (dispatch, getState) => {
    dispatch(startRemoteOperation(id))
    return fetchClient()
      .request(routes.campaign.api.chip_in({ id }), {
        method: 'POST',
        body: { ...fields },
      })
      .then(response => {
        const { payment_id: paymentId } = response
        const { amount, anonymous } = fields
        const date = new Date().toISOString()
        const txn = {
          paymentId,
          pending: true,
          amount: parseFloat(amount),
          campaignId: id,
          anonymous,
          date,
        }
        const sender = {
          id: getState().current_user.id,
          name:
            getState().current_user.business_name ||
            getState().current_user.full_name,
          photo: getState().current_user.profile_photo,
        }
        saveTxnInCookie(id, getState().current_user.id, txn)
        dispatch({
          type: FINISH_CHIP_IN_SUCCESSFULLY,
          payload: { txn, sender },
        })
        dispatch(finishRemoteOperation(id))
      })
      .catch(err => {
        dispatch(failedCampaignRemoteOperation(id, err))
        return Promise.reject(err)
      })
  }
}
